import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  Skeleton,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  School,
  PlayArrow,
  CheckCircle,
  Circle,
  Book,
  Schedule,
  Star,
  TrendingUp,
  Assignment,
  Refresh,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import CourseEvaluation from '../components/CourseEvaluation';
import CertificateDialog from '../components/CertificateDialog';

interface Lesson {
  id: string;
  title: string;
  description: string;
  duration: number;
  isCompleted: boolean;
  order: number;
}

interface Course {
  id: string;
  title: string;
  description: string;
  category: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  duration: number;
  modules: number;
  rating: number;
  enrolledUsers: number;
  lessons: Lesson[];
  progress: number;
  isEnrolled: boolean;
}

const CourseDetail: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [showEvaluation, setShowEvaluation] = useState(false);
  const [evaluationCompleted, setEvaluationCompleted] = useState(false);
  const [showEnrollDialog, setShowEnrollDialog] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  
  // Estados para certificado
  const [certificateDialogOpen, setCertificateDialogOpen] = useState(false);
  const [certificateData, setCertificateData] = useState<any>(null);
  const [certificateEligible, setCertificateEligible] = useState(false);
  const [certificateError, setCertificateError] = useState('');
  
  // Verificar si la empresa permite ver certificados
  const allowViewCertificates = user?.company?.settings?.allowViewCertificates ?? true;

  useEffect(() => {
    fetchCourseDetail();
  }, [courseId]);

  // Solo refrescar cuando sea necesario (por ejemplo, al regresar de una lección)
  // Eliminamos el refrescado automático para evitar saturar el servidor
  
  // Función para refrescar el progreso manualmente cuando sea necesario
  const refreshProgress = async () => {
    if (course) {
      console.log('🔄 Refrescando progreso del curso manualmente...');
      await fetchCourseDetail();
    }
  };

  const fetchCourseDetail = async () => {
    try {
      setLoading(true);
      const response = await api.getCourse(courseId || '');
      
      // Obtener el progreso del usuario para este curso
      let userProgress = null;
      let completedLessons: string[] = [];
      let progressPercentage = 0;
      
      try {
        const progressResponse = await api.getCourseProgress(courseId || '');
        userProgress = progressResponse;
        
        // Extraer lecciones completadas del lessonsProgress
        completedLessons = progressResponse.lessonsProgress
          ?.filter((lp: any) => lp.status === 'completed')
          .map((lp: any) => lp.lesson.toString()) || [];
        
        progressPercentage = progressResponse.completionPercentage || 0;
        setCertificateEligible(Boolean(progressResponse.certificateEligible));
        console.log('📊 Progreso del usuario:', progressResponse);
        console.log('📋 Lecciones completadas:', completedLessons);
      } catch (progressError) {
        console.log('⚠️ No se pudo obtener el progreso del usuario:', progressError);
        setCertificateEligible(false);
      }
      
      // Transformar los datos para que coincidan con la interfaz
      const transformedCourse = {
        id: response.id || courseId || '',
        title: response.title,
        description: response.description,
        category: response.category,
        difficulty: response.difficulty,
        duration: response.estimatedDuration,
        modules: response.lessons?.length || 0,
        rating: response.analytics?.averageRating || 0,
        enrolledUsers: response.analytics?.enrollments || 0,
        progress: progressPercentage,
        isEnrolled: !!userProgress,
        lessons: response.lessons?.map((lesson: any) => ({
          id: lesson._id || lesson.id || lesson.order?.toString() || '',
          title: lesson.title,
          description: lesson.content?.substring(0, 100) + '...' || lesson.title,
          duration: lesson.duration,
          isCompleted: completedLessons.includes(lesson._id || lesson.id || lesson.order?.toString() || ''),
          order: lesson.order,
        })) || [],
      };
      
      console.log('📚 Curso transformado:', transformedCourse);
      setCourse(transformedCourse);
    } catch (err: any) {
      console.error('Error al cargar el curso:', err);
    } finally {
      setLoading(false);
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'success';
      case 'intermediate':
        return 'warning';
      case 'advanced':
        return 'error';
      default:
        return 'default';
    }
  };

  const getDifficultyText = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'Principiante';
      case 'intermediate':
        return 'Intermedio';
      case 'advanced':
        return 'Avanzado';
      default:
        return difficulty;
    }
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const handleStartLesson = (lessonId: string) => {
    // Navegar a la página de la lección
    navigate(`/courses/${courseId}/lessons/${lessonId}`);
  };

  const getNextIncompleteLesson = () => {
    if (!course) return null;
    
    // Encontrar la primera lección no completada
    const nextLesson = course.lessons.find(lesson => !lesson.isCompleted);
    console.log('🔍 Siguiente lección incompleta:', nextLesson);
    return nextLesson;
  };

  const handleBackToCourses = () => {
    navigate('/courses');
  };

  const handleStartEvaluation = () => {
    setShowEvaluation(true);
  };

  const handleEvaluationComplete = (result: any) => {
    setEvaluationCompleted(true);
    setShowEvaluation(false);
    // Aquí podrías actualizar el progreso del curso
    console.log('Evaluación completada:', result);
  };

  const handleCloseEvaluation = () => {
    setShowEvaluation(false);
  };

  const handleEnrollCourse = async () => {
    try {
      setEnrolling(true);
      console.log('📝 Inscribiendo al curso:', courseId);
      
      // Llamar al endpoint de inscripción
      await api.enrollInCourse(courseId || '');
      
      console.log('✅ Inscripción exitosa');
      
      // Cerrar el diálogo
      setShowEnrollDialog(false);
      
      // Actualizar solo el estado local en lugar de refrescar todo el curso
      if (course) {
        setCourse({
          ...course,
          isEnrolled: true,
          progress: 0 // El usuario recién se inscribió, progreso inicial
        });
      }
      
    } catch (error: any) {
      console.error('❌ Error al inscribirse al curso:', error);
      // Aquí podrías mostrar un mensaje de error al usuario
    } finally {
      setEnrolling(false);
    }
  };

  const handleGenerateCertificate = async () => {
    if (!course) return;
    
    try {
      setCertificateError('');
      const certificateData = await api.generateCertificate(course.id);
      setCertificateData(certificateData);
      setCertificateDialogOpen(true);
    } catch (error: any) {
      console.error('Error generando certificado:', error);
      setCertificateError(
        error.response?.data?.message ||
          'No puede obtener el certificado hasta aprobar todas las evaluaciones del curso.'
      );
    }
  };

  const handleStartCourse = () => {
    if (!course?.isEnrolled) {
      // Si no está inscrito, mostrar diálogo de confirmación
      setShowEnrollDialog(true);
    } else {
      // Si ya está inscrito, continuar con el curso
      const nextLesson = getNextIncompleteLesson();
      if (nextLesson) {
        console.log('🚀 Navegando a lección:', nextLesson.title);
        handleStartLesson(nextLesson.id);
      } else {
        console.log('✅ Todas las lecciones están completadas');
        // Si todas están completadas, ir a la primera lección
        handleStartLesson(course.lessons[0].id);
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width="60%" height={40} />
        <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
        <Skeleton variant="text" width="80%" sx={{ mt: 2 }} />
        <Skeleton variant="text" width="60%" />
      </Box>
    );
  }

  if (!course) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Curso no encontrado</Alert>
      </Box>
    );
  }

  const completedLessons = course.lessons.filter(lesson => lesson.isCompleted).length;
  const totalLessons = course.lessons.length;

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={handleBackToCourses}
          sx={{ cursor: 'pointer' }}
        >
          Cursos
        </Link>
        <Typography color="text.primary">{course.title}</Typography>
      </Breadcrumbs>

      {/* Header del curso */}
      <Card sx={{ mb: 3 }}>
        <Box
          sx={{
            height: 200,
            background: `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            position: 'relative',
          }}
        >
          <School sx={{ fontSize: 80 }} />
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              display: 'flex',
              gap: 1,
            }}
          >
            <Chip
              label={getDifficultyText(course.difficulty)}
              color={getDifficultyColor(course.difficulty) as any}
              size="small"
            />
            <Chip
              label={course.category}
              variant="outlined"
              size="small"
              sx={{ color: 'white', borderColor: 'white' }}
            />
          </Box>
        </Box>

        <CardContent>
          <Typography variant="h4" gutterBottom>
            {course.title}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {course.description}
          </Typography>

          {/* Estadísticas del curso */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Schedule color="action" />
              <Typography variant="body2" color="text.secondary">
                {formatDuration(course.duration)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Book color="action" />
              <Typography variant="body2" color="text.secondary">
                {course.modules} módulos
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingUp color="action" />
              <Typography variant="body2" color="text.secondary">
                {course.enrolledUsers} estudiantes
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Star color="warning" />
              <Typography variant="body2" color="text.secondary">
                {course.rating.toFixed(1)} ({course.enrolledUsers} reseñas)
              </Typography>
            </Box>
          </Box>

          {/* Progreso del curso */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Progreso del curso
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title="Actualizar progreso">
                  <IconButton 
                    size="small" 
                    onClick={refreshProgress}
                    sx={{ color: 'primary.main' }}
                  >
                    <Refresh />
                  </IconButton>
                </Tooltip>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {completedLessons} de {totalLessons} lecciones completadas
                  </Typography>
                  {completedLessons === totalLessons && (
                    <Chip
                      label={certificateEligible ? '¡Curso completado!' : 'Evaluaciones pendientes'}
                      color={certificateEligible ? 'success' : 'warning'}
                      size="small"
                    />
                  )}
                </Box>
              </Box>
            </Box>
            <LinearProgress
              variant="determinate"
              value={(completedLessons / totalLessons) * 100}
              sx={{ height: 8, borderRadius: 4 }}
              color={completedLessons === totalLessons ? 'success' : 'primary'}
            />
          </Box>

          {/* Botón de acción principal */}
          {certificateEligible ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              {certificateError && (
                <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
                  {certificateError}
                </Alert>
              )}
              {/* Sección de felicitaciones */}
              <Box sx={{ 
                background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                borderRadius: 3,
                p: 4,
                mb: 3,
                color: 'white',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Efectos decorativos */}
                <Box sx={{
                  position: 'absolute',
                  top: -20,
                  right: -20,
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '3rem'
                }}>
                  🎉
                </Box>
                <Box sx={{
                  position: 'absolute',
                  bottom: -30,
                  left: -30,
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '2rem'
                }}>
                  🏆
                </Box>
                
                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2, position: 'relative', zIndex: 1 }}>
                  ¡Felicitaciones!
                </Typography>
                <Typography variant="h6" sx={{ mb: 2, position: 'relative', zIndex: 1 }}>
                  Has completado exitosamente el curso
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, position: 'relative', zIndex: 1 }}>
                  "{course?.title}"
                </Typography>
                
                {allowViewCertificates ? (
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleGenerateCertificate}
                    sx={{
                      background: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      border: '2px solid white',
                      fontWeight: 'bold',
                      px: 4,
                      py: 1.5,
                      '&:hover': {
                        background: 'rgba(255,255,255,0.3)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.2)'
                      },
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      zIndex: 1
                    }}
                  >
                    🏆 Generar Certificado
                  </Button>
                ) : (
                  <Box sx={{ 
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: 2,
                    p: 3,
                    mt: 2,
                    position: 'relative',
                    zIndex: 1
                  }}>
                    <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
                      Gracias por completar el curso. Los nuevos conocimientos y habilidades que has aprendido fortalecen tu ciberseguridad y la de la compañía.
                    </Typography>
                  </Box>
                )}
              </Box>
              
              {/* Estadísticas del curso completado */}
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                gap: 2,
                mb: 3
              }}>
                <Card sx={{ textAlign: 'center', p: 2 }}>
                  <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
                    {totalLessons}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Lecciones Completadas
                  </Typography>
                </Card>
                <Card sx={{ textAlign: 'center', p: 2 }}>
                  <Typography variant="h4" color="primary.main" sx={{ fontWeight: 'bold' }}>
                    100%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Progreso Total
                  </Typography>
                </Card>
                <Card sx={{ textAlign: 'center', p: 2 }}>
                  <Typography variant="h4" color="secondary.main" sx={{ fontWeight: 'bold' }}>
                    🎯
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Objetivos Cumplidos
                  </Typography>
                </Card>
              </Box>
            </Box>
          ) : completedLessons === totalLessons ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>
                Ha completado todas las lecciones, pero debe aprobar todas las evaluaciones con la nota mínima requerida para obtener su certificado.
              </Alert>
              <Button
                variant="contained"
                size="large"
                startIcon={<PlayArrow />}
                onClick={handleStartCourse}
              >
                Continuar curso
              </Button>
            </Box>
          ) : (
            <Button
              variant="contained"
              size="large"
              startIcon={<PlayArrow />}
              sx={{
                background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #5a6fd8 30%, #6a4190 90%)',
                },
              }}
              onClick={handleStartCourse}
            >
              {completedLessons === 0 ? 'Comenzar Curso' : 'Continuar Curso'}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Lista de lecciones */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Lecciones del curso
          </Typography>
          <List>
            {course.lessons.map((lesson, index) => (
              <React.Fragment key={lesson.id}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleStartLesson(lesson.id)}
                    sx={{
                      borderRadius: 1,
                      mb: 1,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <ListItemIcon>
                      {lesson.isCompleted ? (
                        <CheckCircle color="success" />
                      ) : (
                        <Circle color="action" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1">
                            {index + 1}. {lesson.title}
                          </Typography>
                          <Chip
                            label={formatDuration(lesson.duration)}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={lesson.description}
                    />
                    <Button
                      variant={lesson.isCompleted ? "outlined" : "contained"}
                      size="small"
                      startIcon={lesson.isCompleted ? <CheckCircle /> : <PlayArrow />}
                    >
                      {lesson.isCompleted ? 'Completada' : 'Iniciar'}
                    </Button>
                  </ListItemButton>
                </ListItem>
                {index < course.lessons.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Dialog de confirmación de inscripción */}
      <Dialog
        open={showEnrollDialog}
        onClose={() => setShowEnrollDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <School sx={{ color: 'primary.main' }} />
            <Typography variant="h6">
              Confirmar Inscripción
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            ¿Estás seguro de que deseas inscribirte en el curso <strong>"{course.title}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Una vez inscrito, podrás acceder a todas las lecciones del curso y tu progreso será registrado automáticamente.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => setShowEnrollDialog(false)}
              disabled={enrolling}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleEnrollCourse}
              disabled={enrolling}
              sx={{
                background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #5a6fd8 30%, #6a4190 90%)',
                },
              }}
            >
              {enrolling ? 'Inscribiendo...' : 'Confirmar Inscripción'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>


      {/* Diálogo de certificado */}
      {certificateData && (
        <CertificateDialog
          open={certificateDialogOpen}
          onClose={() => {
            setCertificateDialogOpen(false);
            setCertificateData(null);
          }}
          studentName={certificateData.studentName}
          courseName={certificateData.courseName}
          completionDate={certificateData.completionDate}
          position={certificateData.position}
          companyName={certificateData.company?.name}
          companyLogo={certificateData.company?.logo}
          certificateSigner={certificateData.company?.certificateSigner}
          showCTOSignature={certificateData.company?.showCTOSignature ?? true}
          allowViewCertificates={allowViewCertificates}
        />
      )}
    </Box>
  );
};

export default CourseDetail; 